const BOOKING_OR_SHOPPING_ID_REGEXP = /^(([\da-f]{4}-?)+|\d+)$/i;
const BOOKING_ID_REGEXP = /^\d+$/i;

const getHashParams = (hash) =>
  hash
    .replace(/^#/, '')
    .split('/')
    .filter((chunk) => !!chunk);
const getBookingIdFromLastParam = (params) => params[params.length - 1];
const getFirstParamAsBookingId = (params, pos = 0) => {
  const param = params[pos];
  if (param === undefined) return null;
  return BOOKING_ID_REGEXP.test(param)
    ? param
    : getFirstParamAsBookingId(params, pos + 1);
};
const getBookingIdFromPriceFreeze = (params) => {
  return params[0] === 'pfpayment' ? getBookingIdFromLastParam(params) : null;
};
const getTransactionIdInPathRegexMap = (pathRegexToFnMap) => (pathname) => {
  for (const [regex, fn] of pathRegexToFnMap) {
    if (regex.test(pathname)) {
      return fn;
    }
  }
};

const accommodationPagesWithBookingId = new Set([
  'hotel_guests',
  'hotel_payment',
  'hotel_prime',
]);
const getAccommodationBookingIdFromPathname = (pathname) => {
  for (const page of accommodationPagesWithBookingId) {
    const segment = new RegExp(`/${page}/`, 'i');
    if (segment.test(pathname)) {
      return pathname.split('/').pop();
    }
  }

  return null;
};
const getBookingIdFromAccommodation = (params, pathname) =>
  accommodationPagesWithBookingId.has(params[0])
    ? getBookingIdFromLastParam(params)
    : getAccommodationBookingIdFromPathname(pathname);
const flightsAndDynpackPagesWithBookingId = new Set([
  'checkout_payment',
  'checkout_passengers',
  'flights_info',
  'non_essential_products',
  'flexibility_bundle',
  'canx',
  'insurances',
  'flex_dates',
  'additions',
  'support_pack',
  'disruption_compensation',
  'ancillaries_hub',
  'flexibility_bundle_hub',
  'insurances_hub',
  'canx_hub',
  'support_pack_hub',
  'additions_hub',
  'disruption_compensation_hub',
]);
const getBookingIdFromFlightsOrDynpack = (params) =>
  flightsAndDynpackPagesWithBookingId.has(params[0])
    ? getFirstParamAsBookingId(params)
    : null;

const getBookingIdFromTravelSecure = (params) => {
  const bookingId = getBookingIdFromLastParam(params);
  return BOOKING_OR_SHOPPING_ID_REGEXP.test(bookingId) ? bookingId : null;
};
const getSearchIdFromPriceFreeze = (params) => params[1];

const pathRegexWithBookingIdMap = new Map([
  [/^\/travel\/secure\/$/i, getBookingIdFromTravelSecure],
  [/^\/travel\/flights\/checkout\/$/i, getBookingIdFromFlightsOrDynpack],
  [/^\/travel\/dynpack\/$/i, getBookingIdFromFlightsOrDynpack],
  [/^\/travel\/price-freeze\/$/i, getBookingIdFromPriceFreeze],
  [/^\/travel\/accommodation\//i, getBookingIdFromAccommodation],
]);
const pathRegexWithSearchIdMap = new Map([
  [/^\/travel\/price-freeze\/$/i, getSearchIdFromPriceFreeze],
]);
const createTransactionIdFromURLGetter =
  (getTransactionIdByPathname) =>
  ({ pathname, hash }) =>
    getTransactionIdByPathname(pathname)?.(getHashParams(hash), pathname);

const getBookingIdFromURL = createTransactionIdFromURLGetter(
  getTransactionIdInPathRegexMap(pathRegexWithBookingIdMap)
);
const getSearchIdFromURL = createTransactionIdFromURLGetter(
  getTransactionIdInPathRegexMap(pathRegexWithSearchIdMap)
);

const getTransactionIdFromURL = (url) => {
  const bookingId = getBookingIdFromURL(url);
  if (bookingId) {
    return { bookingId };
  }
  const searchId = getSearchIdFromURL(url);
  if (searchId) {
    return { searchId };
  }
  return null;
};

const getStoredBookingId = () => {
  const bookingId = window?.__htmlEvents__?.bookingId?.toString();
  return bookingId ? { bookingId } : {};
};

const getStoredSearchId = () => {
  const searchId = window?.__htmlEvents__?.searchId?.toString();
  return searchId ? { searchId } : {};
};

const getStoredMembershipId = () => {
  const membershipId = window?.__htmlEvents__?.membershipId?.toString();
  return membershipId ? { membershipId } : {};
};

export const setTransactionBookingId = (bookingId) => {
  window.__htmlEvents__ = {
    ...window.__htmlEvents__,
    bookingId,
  };
};

export const setTransactionSearchId = (searchId) => {
  window.__htmlEvents__ = {
    ...window.__htmlEvents__,
    searchId,
  };
};

export const setTransactionMembershipId = (membershipId) => {
  window.__htmlEvents__ = {
    ...window.__htmlEvents__,
    membershipId,
  };
};

const pathRegexForStoredTransactionIdMap = new Map([
  [/^\/travel\/secure\/$/i, getStoredBookingId],
  [/^\/travel\/$/i, getStoredSearchId],
  [/^\/travel\/accommodation\//i, getStoredSearchId],
  [/^\/travel\/membership-pages\//i, getStoredMembershipId],
  [/^\/travel\/profile\/$/i, getStoredMembershipId],
]);
const getStoredTransactionId = (pathname) => {
  return (
    getTransactionIdInPathRegexMap(pathRegexForStoredTransactionIdMap)(
      pathname
    )?.() ?? {}
  );
};

export const getTransactionId = () => {
  const url = new URL(window.location.href);
  return (
    getTransactionIdFromURL(url) ?? getStoredTransactionId(url.pathname) ?? {}
  );
};
